import { useMemo, ReactNode } from 'react';

import Box from '@mui/material/Box';
import { sp } from '@/theme/mui';
import Stack from '@mui/material/Stack';

import { Address } from '@/components/atoms/Address/Address';
import { ImageGallery } from '@/components/atoms/ImageGallery/ImageGallery';
import { EmptyGalleryPlaceholder } from '@/components/atoms/ImageGallery/EmptyGalleryPlaceholder';
import { Map } from '@/components/atoms/Map/Map';
import { ShowMore } from '@/components/atoms/ShowMore/ShowMore';
import { Title } from '@/components/atoms/Title/Title';
import { Divider } from '@/components/atoms/Divider/Divider';
import { Quote } from '@/components/atoms/Quote/Quote';

import { Button } from '@/components/atoms/Button/Button';
import { CareEnquiriesCard } from '@/components/molecules/CareEnquiriesCard/CareEnquiriesCard';
import { CareHomeFeatures } from '@/components/molecules/CareHomeFeatures/CareHomeFeatures';
import { CareHomeFunding } from '@/components/molecules/CareHomeFunding/CareHomeFunding';
import { CareServiceTypes } from '@/components/molecules/CareServiceTypes/CareServiceTypes';
import { RegulatorsRatings } from '@/components/molecules/RegulatorsRatings/RegulatorsRatings';
import { Specialisms } from '@/components/molecules/CareProviderSpecialisms/Specialisms';
import { Summary } from '@/components/molecules/CareProviderSummary/Summary';
import { CareProviderProfileSection } from '@/components/molecules/CareProviderProfileSection/CareProviderProfileSection';

const renderIf = (enabled?: any) => {
  return ({ children }: { children: ReactNode }) => (
    <>{enabled ? children : null}</>
  );
};

interface DirectoryProfileProps {
  provider: any;
  googleMapsApiKey: string;
  markerImageUrl?: string;
  onBack?: () => void;
}
export function DirectoryProfile({
  provider,
  googleMapsApiKey,
  markerImageUrl,
  onBack,
}: DirectoryProfileProps) {
  const {
    name,
    enquiryEmail,
    enquiryPhone,
    photos = [],
    location,
    description,
    careServiceTypes,
    managerQuote,
    managerName,
    address,
    ratings,
    specialisms,
    pricePerWeek,
    bedCount,
    dateOpened,
    availability,
    doubleBedCount,
    enSuiteRoomCount,
    purposeBuilt,
    careType,
    hourlyRate,
    minimumVisitTime,
    regulatoryId,
  } = provider;
  const isCareHome = careType === 'careHome';

  const lastSearchRegulator = useMemo<
    'ci' | 'cqc' | 'rqia' | 'ci_wales' | undefined
  >(() => {
    if (regulatoryId[0] === 'C') {
      return 'ci';
    }
    if (regulatoryId[1] === '-') {
      return 'cqc';
    }
    return undefined;
  }, [regulatoryId]);
  const haveImages = photos.length > 0;
  const showMap = !!googleMapsApiKey && !!location && isCareHome;
  const shouldRenderMapAndGallery = showMap || haveImages;
  const RenderMapAndGallery = renderIf(shouldRenderMapAndGallery);
  const RenderOwnersDescription = renderIf(!!description);
  const RenderCareServiceTypes = renderIf(
    !!careServiceTypes && careServiceTypes.length > 0 && isCareHome,
  );
  const RenderManagerQuote = renderIf(!!managerQuote);
  const RenderCareHomeFunding = renderIf(!!lastSearchRegulator && isCareHome);
  const RenderRatings = renderIf(!!ratings);
  const RenderSpecialisms = renderIf(!!specialisms && specialisms.length > 0);
  const RenderCareHomeFeatures = renderIf(isCareHome);

  const careHomeFeatures = {
    bedCount,
    dateOpened,
    purposeBuilt,
    doubleBedCount,
    enSuiteRoomCount,
  };

  const summary = {
    rating: ratings.overallRating,
    pricePerWeek,
    bedCount,
    dateOpened,
    availability,
    hourlyRate,
    managerName,
    minimumVisitTime: minimumVisitTime?.label,
  };

  return (
    <Stack sx={{ gap: sp[16], typography: 'bodyMd' }}>
      <Box
        component='a'
        id='top-of-page-marker'
        sx={{ position: 'absolute', '&:focus': { zIndex: 1 } }}
      />
      {onBack && (
        <Button
          ariaLabel='back'
          iconLeft='chevron-left'
          variant='secondary'
          size='sm'
          onPress={onBack}
        >
          Back
        </Button>
      )}
      <RenderMapAndGallery>
        <Box sx={{ pb: sp[16] }}>
          {false && googleMapsApiKey && location && (
            <div style={{ width: '300px', height: '300px' }}>
              <Map
                coords={location}
                apiKey={googleMapsApiKey}
                markerImageUrl={markerImageUrl}
              />
            </div>
          )}
          {haveImages ? (
            <ImageGallery images={photos} />
          ) : (
            <EmptyGalleryPlaceholder description="This care provider hasn't added any images" />
          )}
        </Box>
      </RenderMapAndGallery>
      <div>
        <Title>{name}</Title>
        <Address address={address} />
        <Divider isOr={false} margin={16} />
        <CareProviderProfileSection title='Summary'>
          <Summary summaryItems={summary} careType={careType} />
          <CareEnquiriesCard
            providerName={name}
            email={enquiryEmail}
            phoneNumber={enquiryPhone}
            regulatoryId={regulatoryId}
          />
        </CareProviderProfileSection>
      </div>
      <RenderOwnersDescription>
        <CareProviderProfileSection title="Owner's description">
          <ShowMore text={description} />
        </CareProviderProfileSection>
      </RenderOwnersDescription>
      <RenderCareServiceTypes>
        <CareProviderProfileSection title='Types of care provided'>
          <CareServiceTypes serviceTypes={careServiceTypes} />
        </CareProviderProfileSection>
      </RenderCareServiceTypes>
      <RenderSpecialisms>
        <CareProviderProfileSection title='Services and specialisms'>
          <Specialisms specialisms={specialisms} />
        </CareProviderProfileSection>
      </RenderSpecialisms>
      <RenderCareHomeFeatures>
        <CareProviderProfileSection title='Care home features'>
          <CareHomeFeatures careHomeFeatures={careHomeFeatures} />
        </CareProviderProfileSection>
      </RenderCareHomeFeatures>
      <RenderManagerQuote>
        <CareProviderProfileSection title="Manager's quote">
          <Quote author={managerName} title='Care home manager'>
            {managerQuote}
          </Quote>
        </CareProviderProfileSection>
      </RenderManagerQuote>
      <RenderCareHomeFunding>
        <CareHomeFunding
          pricePerWeek={pricePerWeek}
          regulator={lastSearchRegulator}
        />
      </RenderCareHomeFunding>
      <RenderRatings>
        <CareProviderProfileSection title="Regulator's inspection rating">
          <RegulatorsRatings ratings={ratings} />
        </CareProviderProfileSection>
      </RenderRatings>
    </Stack>
  );
}

export default DirectoryProfile;
